import api, { unwrap } from './client';
import type { ApiResponse } from '@/types/api';
import type {
  EmployeeSalesReportDto,
  ExpiryReportDto,
  InventoryReportDto,
  LowStockReportDto,
  ProductSalesReportDto,
  ProfitReportDto,
  SalesReportDto,
  TaxReportDto,
} from '@/types/report';

export type ExportFormat = 'csv' | 'excel' | 'pdf';

async function downloadBlob(url: string, params: Record<string, unknown>, filename: string) {
  const response = await api.get(url, { params, responseType: 'blob' });
  const blob = new Blob([response.data]);
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

export const reportsApi = {
  sales: (from: string, to: string, storeId?: number) =>
    unwrap(
      api.get<ApiResponse<SalesReportDto>>('/v1/reports/sales', {
        params: { from, to, storeId },
      }),
    ),

  inventory: (storeId?: number) =>
    unwrap(
      api.get<ApiResponse<InventoryReportDto>>('/v1/reports/inventory', {
        params: storeId ? { storeId } : undefined,
      }),
    ),

  productSales: (from: string, to: string, storeId?: number) =>
    unwrap(
      api.get<ApiResponse<ProductSalesReportDto>>('/v1/reports/products/sales', {
        params: { from, to, storeId },
      }),
    ),

  profit: (from: string, to: string, storeId?: number) =>
    unwrap(
      api.get<ApiResponse<ProfitReportDto>>('/v1/reports/profit', {
        params: { from, to, storeId },
      }),
    ),

  tax: (from: string, to: string, storeId?: number) =>
    unwrap(
      api.get<ApiResponse<TaxReportDto>>('/v1/reports/tax', {
        params: { from, to, storeId },
      }),
    ),

  lowStock: (storeId?: number) =>
    unwrap(api.get<ApiResponse<LowStockReportDto>>('/v1/reports/low-stock', { params: { storeId } })),

  expiry: (storeId?: number, withinDays = 30) =>
    unwrap(
      api.get<ApiResponse<ExpiryReportDto>>('/v1/reports/expiry', {
        params: { storeId, withinDays },
      }),
    ),

  employeeSales: (from: string, to: string, storeId?: number) =>
    unwrap(
      api.get<ApiResponse<EmployeeSalesReportDto>>('/v1/reports/employees/sales', {
        params: { from, to, storeId },
      }),
    ),

  exportSales: (from: string, to: string, format: ExportFormat, storeId?: number) =>
    downloadBlob('/v1/reports/sales/export', { from, to, format, storeId }, `sales-${from}-${to}.${format === 'excel' ? 'xlsx' : format}`),

  exportInventory: (format: ExportFormat, storeId?: number) =>
    downloadBlob('/v1/reports/inventory/export', { format, storeId }, `inventory.${format === 'excel' ? 'xlsx' : format}`),
};
